import { useState } from "react";
import { Link, useParams } from "react-router";
import { usePuzzle, useSetPuzzleStatus } from "../api/queries";
import type { AnswerValue, PuzzleDetail, PuzzleStatus } from "../api/schemas";
import { useAuth, useCan } from "../auth/AuthContext";
import { STATUS_TRANSITIONS } from "../auth/permissions";
import { PickRateBars } from "../components/charts/charts";
import {
  Button,
  CategoryTag,
  Chip,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Stat,
  StatusChip,
  TierDots,
} from "../components/ui";
import { Card } from "../components/ui";
import { formatDateTime, formatLongDate } from "../lib/dates";
import { fmtInt, fmtMs, fmtPct, formatAnswer } from "../lib/format";
import { useHotkey } from "../lib/hotkeys";
import { canonicalAnswer, isSupported, missingCapabilities, PuzzleView } from "@lumen/puzzle-render";
import styles from "./pages.module.css";

const ACTION: Record<PuzzleStatus, { label: string; tone: "primary" | "danger" | "secondary"; verb: string }> = {
  approved: { label: "Approve", tone: "primary", verb: "approve" },
  draft: { label: "Back to draft", tone: "secondary", verb: "move back to draft" },
  rejected: { label: "Reject", tone: "danger", verb: "reject" },
  retired: { label: "Retire", tone: "danger", verb: "retire" },
  published: { label: "Publish", tone: "primary", verb: "publish" },
};

export function PuzzleDetailPage() {
  const { id = "" } = useParams();
  const q = usePuzzle(id);
  const [reveal, setReveal] = useState(false);
  useHotkey("r", () => setReveal((v) => !v), Boolean(q.data));

  if (q.isPending) {
    return (
      <>
        <PageHeader title={<span className="mono">{id}</span>} meta={<Link to="/puzzles">← Puzzles</Link>} />
        <Loading rows={10} />
      </>
    );
  }
  if (q.isError) {
    return (
      <>
        <PageHeader title={<span className="mono">{id}</span>} meta={<Link to="/puzzles">← Puzzles</Link>} />
        <ErrorState
          error={q.error}
          onRetry={() => void q.refetch()}
          title={(q.error as { status?: number }).status === 404 ? "No such puzzle" : "Couldn't load this puzzle"}
        />
      </>
    );
  }

  const { puzzle, stats } = q.data;
  return (
    <>
      <PageHeader
        title={<span className={styles.monoTitle}>{puzzle.id}</span>}
        meta={
          <>
            <Link to="/puzzles" className={styles.back}>
              ← Puzzles
            </Link>
            <StatusChip status={puzzle.status} />
            {puzzle.puzzleNumber ? <span className="mono">#{puzzle.puzzleNumber}</span> : null}
            <span className="mono">{puzzle.date ? formatLongDate(puzzle.date) : "unscheduled"}</span>
            <span className="mono">{puzzle.type}</span>
            <CategoryTag category={puzzle.category} />
          </>
        }
        actions={<StatusActions puzzle={puzzle} />}
      />

      <div className={styles.detailGrid}>
        <Card
          title="Preview"
          className={styles.stageCard}
          actions={
            <Button size="sm" variant={reveal ? "primary" : "secondary"} aria-pressed={reveal} onClick={() => setReveal((v) => !v)} shortcut="R">
              {reveal ? "Hide answer" : "Reveal answer"}
            </Button>
          }
        >
          <div className={styles.stage}>
            <PuzzleView spec={puzzle.spec} answer={puzzle.answer} reveal={reveal} />
          </div>
          <div className={styles.answerLine}>
            <span className="monoLabel">Answer</span>
            {reveal ? <AnswerText answer={puzzle.answer} /> : <span className={styles.hidden}>hidden · press R</span>}
          </div>
        </Card>

        <div className={styles.detailSide}>
          <Card title="Explanation">
            {puzzle.explanation ? <p className={styles.prose}>{puzzle.explanation}</p> : <p className={styles.faint}>No explanation yet.</p>}
          </Card>

          <Card title="Difficulty">
            <div className={styles.statsRow}>
              <Stat label="Score" value={puzzle.spec.difficulty.score.toFixed(2)} sub="0 trivial · 1 hardest" />
              <Stat label="Tier" value={<TierDots tier={puzzle.spec.difficulty.tier} />} sub={`tier ${puzzle.spec.difficulty.tier ?? "—"} of 5`} />
              <Stat label="Time budget" value={fmtMs(puzzle.spec.timeBudgetMs)} sub="700 pts at budget" />
            </div>
            <div className={styles.meterWide}>
              <span style={{ width: `${puzzle.spec.difficulty.score * 100}%` }} />
            </div>
            <div className={styles.requires}>
              {puzzle.spec.requires.map((r) => (
                <Chip key={r} tone={isSupported(r) ? "muted" : "danger"} title={isSupported(r) ? "Rendered by @lumen/puzzle-render" : "Not in the renderer's capabilities"}>
                  {r}
                </Chip>
              ))}
            </div>
            {missingCapabilities(puzzle.spec).length ? (
              <p className={styles.requiresMissing} role="note">
                The web renderer lacks {missingCapabilities(puzzle.spec).join(", ")}. Clients without these components never receive this puzzle.
              </p>
            ) : null}
          </Card>

          <StatsCard stats={stats} answer={puzzle.answer} status={puzzle.status} />
          <HistoryCard puzzle={puzzle} />

          <details className={styles.json}>
            <summary>
              <span className="monoLabel">Spec JSON</span>
            </summary>
            <pre>{JSON.stringify(puzzle.spec, null, 2)}</pre>
          </details>
        </div>
      </div>
    </>
  );
}

function AnswerText({ answer }: { answer: AnswerValue }) {
  return (
    <span className={styles.answerValue}>
      <span className="mono">{answer.kind}</span>
      <code title={canonicalAnswer(answer)}>{formatAnswer(answer)}</code>
    </span>
  );
}

function StatusActions({ puzzle }: { puzzle: PuzzleDetail["puzzle"] }) {
  const allowed = useCan("content.review");
  const { admin } = useAuth();
  const mutation = useSetPuzzleStatus();
  const [pending, setPending] = useState<PuzzleStatus | null>(null);
  const targets = STATUS_TRANSITIONS[puzzle.status];

  if (!allowed) {
    return (
      <span className={styles.readOnly} data-testid="read-only">
        Read-only · {admin?.role} role
      </span>
    );
  }
  if (targets.length === 0) {
    return <span className={styles.readOnly}>No transitions from {puzzle.status}</span>;
  }

  const confirm = () => {
    if (!pending) return;
    mutation.mutate({ id: puzzle.id, status: pending }, { onSuccess: () => setPending(null) });
  };

  return (
    <>
      {targets.map((t) => (
        <Button
          key={t}
          variant={ACTION[t].tone}
          size="sm"
          onClick={() => {
            mutation.reset();
            setPending(t);
          }}
        >
          {ACTION[t].label}
        </Button>
      ))}
      <ConfirmDialog
        open={pending !== null}
        title={pending ? `${ACTION[pending].label} ${puzzle.id}?` : ""}
        confirmLabel={pending ? ACTION[pending].label : "Confirm"}
        tone={pending && ACTION[pending].tone === "danger" ? "danger" : "primary"}
        busy={mutation.isPending}
        error={mutation.error}
        onCancel={() => setPending(null)}
        onConfirm={confirm}
      >
        <p>
          This will {pending ? ACTION[pending].verb : ""} <code>{puzzle.id}</code>
          {puzzle.date ? ` (${formatLongDate(puzzle.date)})` : ""}: <strong>{puzzle.status}</strong> → <strong>{pending}</strong>.
        </p>
        <p className={styles.faint}>
          {pending === "approved"
            ? "Approved puzzles publish automatically when their day begins in UTC+14."
            : pending === "retired"
              ? "Published answers stay locked; retiring only withdraws the puzzle."
              : "The change is written to the audit log."}
        </p>
      </ConfirmDialog>
    </>
  );
}

function StatsCard({ stats, answer, status }: { stats: PuzzleDetail["stats"]; answer: AnswerValue; status: PuzzleStatus }) {
  const hasPicks = Object.keys(stats.optionPickRate).length > 0;
  return (
    <Card title="Player stats" actions={<span className="monoLabel">canonical option ids</span>}>
      {stats.submitted === 0 ? (
        <EmptyState title="No plays yet">
          {status === "published" ? "Stats appear as soon as submissions come in." : `Stats start when the puzzle is published (now ${status}).`}
        </EmptyState>
      ) : (
        <>
          <div className={styles.statsRow}>
            <Stat label="Submitted" value={fmtInt(stats.submitted)} />
            <Stat label="Correct" value={fmtPct(stats.correctRate, 1)} />
            <Stat label="Avg score" value={fmtInt(stats.avgScore)} sub="of 1000" />
            <Stat label="Median time" value={fmtMs(stats.medianElapsedMs)} />
          </div>
          {hasPicks ? (
            <div className={styles.picks}>
              <PickRateBars rates={stats.optionPickRate} correctId={answer.kind === "choice" ? answer.value : null} />
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

function HistoryCard({ puzzle }: { puzzle: PuzzleDetail["puzzle"] }) {
  const history = puzzle.history;
  return (
    <Card title="Status history">
      {history.length === 0 ? (
        <p className={styles.faint}>No recorded transitions.</p>
      ) : (
        <ol className={styles.timeline}>
          {[...history].reverse().map((h, i) => (
            <li key={`${h.at}-${i}`}>
              <StatusChip status={h.status} />
              <span className="mono">
                {h.from ? <span className={styles.faint}>from {h.from} · </span> : null}
                {h.by ?? "system"}
              </span>
              <span className={styles.faint}>{formatDateTime(h.at)}</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
